/**
 * help.ts — modal that explains what the agent is and how to read the
 * canvas. Mirrors vacuum-demo's HelpModal pattern: a singleton overlay
 * with three close affordances (backdrop click, × button, Escape).
 *
 * Content covers:
 *   - the regime (discovery with a noisy local sensor)
 *   - the model (a GRU DRQN trained in Python, shipped frozen as .tflite)
 *   - the visual encoding (value-graded gold, belief heat-map, sensor
 *     footprint, masked Q-bars)
 *   - the Loop gate + basin-cell taboo (the engineering additions)
 *   - the smoke-test headline (3.35× vs random in the browser)
 *
 * No inference happens here; this is pure DOM.
 */

export class HelpModal {
  readonly el: HTMLElement;
  private onCloseFn: () => void;
  private onKey: (e: KeyboardEvent) => void;

  constructor(onClose: () => void) {
    this.onCloseFn = onClose;
    this.el = document.createElement('div');
    this.el.className = 'overlay help-overlay';
    this.el.innerHTML = `
      <div class="overlay-card help-card">
        <button class="help-close" aria-label="Close">×</button>
        <h2>Active Perception</h2>
        <p class="help-lead">A pretrained GRU DRQN that collects value-graded gold on a grid it can only sense noisily along its trajectory.</p>

        <h3>The regime</h3>
        <p>An agent roams a 20×20 grid. It does <em>not</em> see the grid directly — only a small, noisy, <em>trajectory-coupled</em> sensor that reads cells ahead of its last move and to the sides. <code>P(correct) = 0.85</code>; everything else is a uniform guess. The sensor footprint extends ~5 cells in the facing direction, ~1 cell sideways — so the agent sees further along where it's going than to the sides.</p>
        <p>The agent maintains a Bayesian <strong>belief map</strong> (gold / stone / unknown per cell, decaying with recency). That belief is its only memory within an episode. Outside an episode the agent is <em>stateless</em>: every episode starts from a flat prior and a zeroed GRU hidden state. What persists across episodes is the trained Q-function — nothing else.</p>

        <h3>The model</h3>
        <p>The Q-function is a GRU DRQN: a 48-unit GRU whose hidden state threads across ticks, plus a linear Q-head over 8 single-cell moves (4 cardinal + 4 diagonal). Input is 41-d: previous-action one-hot (so the agent knows which way its sensor is facing) plus a 33-d observation that, for each of the 8 candidate moves, exposes <code>[dest_belief_gold, dest_belief_stone, can_move, const]</code> plus the previous reward.</p>
        <p>Training happens <em>in Python</em>: a hand-written numpy DRQN with Double-DQN targets, episode replay, target-network sync, and BPTT-with-truncation. The trained weights export to a 57 KB <code>.tflite</code> via Keras 3; the browser loads it with LiteRT.js and runs pure inference — no training in the page. The Python-side greedy eval is <strong>2.66× vs random</strong>; in the browser the smoke test reports <strong>3.35×</strong> (the gate below helps more on the smoke-test layouts than on average).</p>

        <h3>The Loop gate</h3>
        <p>The trained policy has one visible failure mode: it sometimes gets stuck in a 4×4–6×6 basin, bouncing off a wall, a stone, or a <em>believed</em> stone. Telemetry on 30 baseline episodes showed ~29% of ticks spent inside such basins. The gate uses a <em>diagnose-then-mask</em> recipe:</p>
        <ul class="help-list">
          <li><strong>Detect</strong> via closed-form OLS slopes on a 10-tick window: <code>speed &lt; 0.25</code> AND <code>gold_in_window &lt; 0.10</code> means "stuck and not making progress."</li>
          <li><strong>Mask</strong> any direction appearing ≥ 4 times in the window. The mask stays on until the agent demonstrably escapes (4 consecutive ticks of recovered speed).</li>
          <li><strong>Tie-break</strong> by recency — pick the action whose destination cell was visited <em>least recently</em>, not argmax-of-rest. Pure recency can't oscillate by construction.</li>
        </ul>
        <p>Net effect: <strong>+4.2%</strong> mean gold collection across 10 seeds (best +18.8%, worst −4.1%). Toggle it off to compare the plain policy against the gated one.</p>

        <h3>Basin-cell taboo (optional)</h3>
        <p>The agent sometimes returns to a basin it just escaped — 42% of escapes get re-entered, spending a median 3 ticks back in the box with 0% gold collected. The optional taboo mask bans the 5×5 neighborhood of every escaped basin for ~14 ticks. It's a <strong>variance reducer</strong>, not an improvement: lifts the worst-case ratio from 0.94× to 0.99× at the cost of capping the best-case from 1.19× to 1.12×. Off by default; flip it on for smoother behavior on adversarial layouts.</p>

        <h3>How to read the canvas</h3>
        <div class="help-legend">
          <span class="help-emoji coin"></span><span><strong>Gold</strong> — radius and label scale with the cell's value in [0, 1]. Fat coins are worth more.</span>
          <span class="help-emoji rock"></span><span><strong>Stone</strong> — pays a graded cost on entry. Radius scales with the cost.</span>
          <span class="help-emoji agent"></span><span><strong>Agent</strong> — heading drawn as a triangle pointing toward the last move direction.</span>
          <span class="help-emoji belief"></span><span><strong>Belief heat-map</strong> — overlays the agent's posterior on the floor: warmer = more believed-gold.</span>
          <span class="help-emoji sensor"></span><span><strong>Sensor footprint</strong> — the cells the agent can currently see. Anisotropic, biased forward.</span>
        </div>

        <h3>Try it</h3>
        <ul class="help-list">
          <li>Paint your own layout (gold / stone / erase) and hit <em>Start</em>. The agent has to generalise; it does not memorise your layout.</li>
          <li>Hit <em>Run 30 episodes</em> for the trained-vs-random ratio on the current layout.</li>
          <li>Flip the <em>Loop gate</em> toggle mid-episode to see the agent start escaping basins instead of bouncing in them.</li>
          <li>Flip the <em>Taboo</em> toggle on adversarial layouts where the gate alone overshoots — it lifts the trained-vs-random ratio off the floor.</li>
          <li>Watch the Q-bars at the top: masked actions dim to grey, so you can see exactly which directions the gate is switching off tick by tick.</li>
        </ul>
      </div>
    `;
    // Click on the dimmed backdrop (not the card) closes; so does the × and Esc.
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) this.close();
    });
    this.el.querySelector<HTMLButtonElement>('.help-close')!.addEventListener('click', () => this.close());
    this.onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this.onKey);
  }

  private close(): void {
    this.onCloseFn();
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.el);
  }

  remove(): void {
    document.removeEventListener('keydown', this.onKey);
    this.el.remove();
  }
}
